/**
 * 확인/취소 모달 컴포넌트
 *
 * 삭제, 재생성 등의 확인 작업에 사용하는 표준화된 모달입니다.
 * 중복되는 확인 모달 패턴을 하나의 컴포넌트로 통합합니다.
 *
 * @module
 * @example
 * // 기본 사용
 * <AppConfirmModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="삭제 확인"
 *   message="정말 삭제하시겠습니까?"
 *   confirmText="삭제"
 *   variant="danger"
 * />
 *
 * @example
 * // 로딩 상태와 함께
 * <AppConfirmModal
 *   isOpen={isOpen}
 *   onClose={close}
 *   onConfirm={handleRegenerate}
 *   title="재생성"
 *   message="새 버전을 생성하시겠습니까?"
 *   confirmText="재생성"
 *   isLoading={isRegenerating}
 *   loadingText="재생성 중..."
 * />
 */

'use client';

import { AppButton } from './AppButton';
import { AppModal } from './AppModal';

// =============================================================================
// Types
// =============================================================================

export type AppConfirmModalVariant = 'primary' | 'danger' | 'warning';

export interface AppConfirmModalProps {
  /** 모달 열림 상태 */
  isOpen: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 확인 버튼 클릭 핸들러 */
  onConfirm: () => void | Promise<void>;
  /** 모달 제목 */
  title: string;
  /** 확인 메시지 (문자열 또는 ReactNode) */
  message: React.ReactNode;
  /** 확인 버튼 텍스트 */
  confirmText?: string;
  /** 취소 버튼 텍스트 */
  cancelText?: string;
  /** 로딩 중 텍스트 */
  loadingText?: string;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 버튼 스타일 variant */
  variant?: AppConfirmModalVariant;
  /** 모달 크기 */
  size?: 'sm' | 'md' | 'lg';
}

// =============================================================================
// Styles
// =============================================================================

const variantStyles: Record<AppConfirmModalVariant, string> = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  warning: 'bg-amber-500 hover:bg-amber-600 text-white',
};

// =============================================================================
// Component
// =============================================================================

export function AppConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  loadingText,
  isLoading = false,
  variant = 'primary',
  size = 'md',
}: AppConfirmModalProps) {
  const handleConfirm = async () => {
    await onConfirm();
    // 확인 후 자동으로 닫지 않음 - 호출자가 결정
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          <AppButton variant="ghost" onClick={onClose} disabled={isLoading}>
            {cancelText}
          </AppButton>
          <AppButton
            className={variantStyles[variant]}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (loadingText || `${confirmText} 중...`) : confirmText}
          </AppButton>
        </>
      }
    >
      {typeof message === 'string' ? (
        <p className="text-neutral-600">{message}</p>
      ) : (
        message
      )}
    </AppModal>
  );
}

export default AppConfirmModal;
